import React from "react";
import {
  FaHourglassHalf,
  FaCheckCircle,
  FaPlay,
  FaFlagCheckered,
  FaMapMarkerAlt,
  FaCar,
  FaBan,
  FaUserSlash,
  FaUserTimes,
  FaTimesCircle,
  FaExchangeAlt,
  FaRupeeSign,
  FaCoins,
  FaPercentage,
  FaBalanceScale,
} from "react-icons/fa";

export const emptyDriverStatusBreakdown = () => ({
  totalRides: 0,
  available: 0,
  accepted: 0,
  arrived: 0,
  started: 0,
  completed: 0,
  cancelled: 0,
  totalRidesFare: 0,
  availableFare: 0,
  acceptedFare: 0,
  arrivedFare: 0,
  startedFare: 0,
  completedFare: 0,
  cancelledFare: 0,
  cancelledByDriver: 0,
  cancelledByDriverFare: 0,
  cancelledByUser: 0,
  cancelledByUserFare: 0,
  declineByYou: 0,
  declineByYouFare: 0,
  acceptedByOther: 0,
  acceptedByOtherFare: 0,
  cancelledByUserBeforeAssigned: 0,
  cancelledByUserBeforeAssignedFare: 0,
  driverEarnings: 0,
  totalCommission: 0,
  commissionPaid: 0,
  commissionPending: 0,
  totalGst: 0,
  gstPaid: 0,
  gstPending: 0,
  totalDue: 0,
  duePaid: 0,
  duePending: 0,
});

const cardBaseClass =
  "rounded-lg p-3 shadow-md text-white transition-transform duration-150";

export const DRIVER_CARD_FILTER_LABELS = {
  totalRides: "Total Rides",
  available: "Available",
  accepted: "Accepted",
  arrived: "Arrived",
  started: "Started",
  completed: "Completed",
  cancelled: "Cancelled (All)",
  cancelledByDriver: "Cancelled by Driver",
  cancelledByUser: "Cancelled by Customer",
  declineByYou: "Decline by You",
  acceptedByOther: "Accepted by Other Driver",
  cancelledByUserBeforeAssigned: "Cancelled by User - Before Driver Assigned",
};

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

export const formatMoney = (value) =>
  currencyFormatter.format(Number(value) || 0);

export const formatStatusSharePercent = (count, total) => {
  const c = Number(count) || 0;
  const t = Number(total) || 0;
  if (c <= 0 || t <= 0) return "0";
  const pct = (c / t) * 100;
  if (pct < 1) return pct.toFixed(2);
  return String(Math.round(pct));
};

export const calcAverageFare = (count, tripAmount) => {
  const c = Number(count) || 0;
  const amount = Number(tripAmount) || 0;
  if (c <= 0) return 0;
  return Math.round((amount / c) * 100) / 100;
};

const statusRow1 = [
  {
    key: "totalRides",
    countKey: "totalRides",
    fareKey: "totalRidesFare",
    label: "Total Rides",
    gradient: "bg-gradient-to-r from-slate-600 to-slate-700",
    icon: FaCar,
  },
  {
    key: "available",
    countKey: "available",
    fareKey: "availableFare",
    label: "Available",
    last24Hours: true,
    gradient: "bg-gradient-to-r from-amber-500 to-amber-600",
    icon: FaHourglassHalf,
  },
  {
    key: "accepted",
    countKey: "accepted",
    fareKey: "acceptedFare",
    label: "Accepted",
    gradient: "bg-gradient-to-r from-blue-500 to-blue-600",
    icon: FaCheckCircle,
  },
  {
    key: "arrived",
    countKey: "arrived",
    fareKey: "arrivedFare",
    label: "Arrived",
    gradient: "bg-gradient-to-r from-teal-500 to-teal-600",
    icon: FaMapMarkerAlt,
  },
];

const startedCard = {
  key: "started",
  countKey: "started",
  fareKey: "startedFare",
  label: "Started",
  gradient: "bg-gradient-to-r from-cyan-500 to-cyan-600",
  icon: FaPlay,
};

const completedCard = {
  key: "completed",
  countKey: "completed",
  fareKey: "completedFare",
  label: "Completed",
  gradient: "bg-gradient-to-r from-green-500 to-green-600",
  icon: FaFlagCheckered,
};

const cancellationCards = [
  {
    key: "cancelled",
    countKey: "cancelled",
    fareKey: "cancelledFare",
    label: "Cancelled (All)",
    gradient: "bg-gradient-to-r from-gray-500 to-gray-600",
    icon: FaBan,
  },
  {
    key: "cancelledByDriver",
    countKey: "cancelledByDriver",
    fareKey: "cancelledByDriverFare",
    label: "Cancelled by Driver",
    gradient: "bg-gradient-to-r from-orange-500 to-orange-600",
    icon: FaUserSlash,
  },
  {
    key: "cancelledByUser",
    countKey: "cancelledByUser",
    fareKey: "cancelledByUserFare",
    label: "Cancelled by Customer",
    gradient: "bg-gradient-to-r from-rose-500 to-rose-600",
    icon: FaUserTimes,
  },
  {
    key: "declineByYou",
    countKey: "declineByYou",
    fareKey: "declineByYouFare",
    label: "Decline by You",
    gradient: "bg-gradient-to-r from-red-500 to-red-600",
    icon: FaTimesCircle,
  },
  {
    key: "acceptedByOther",
    countKey: "acceptedByOther",
    fareKey: "acceptedByOtherFare",
    label: "Accepted by Other Driver",
    last24Hours: true,
    gradient: "bg-gradient-to-r from-violet-500 to-violet-600",
    icon: FaExchangeAlt,
  },
  {
    key: "cancelledByUserBeforeAssigned",
    countKey: "cancelledByUserBeforeAssigned",
    fareKey: "cancelledByUserBeforeAssignedFare",
    label: "Cancelled by User - Before Driver Assigned",
    last24Hours: true,
    gradient: "bg-gradient-to-r from-pink-500 to-pink-600",
    icon: FaUserTimes,
  },
];

const completedFinancialCards = [
  {
    key: "driverEarnings",
    label: "Earnings",
    icon: FaCoins,
    gradient: "bg-gradient-to-r from-emerald-600 to-green-700",
    subItems: [{ label: "Total Earnings", key: "driverEarnings" }],
  },
  {
    key: "totalDue",
    label: "Total Due",
    icon: FaBalanceScale,
    gradient: "bg-gradient-to-r from-rose-500 to-red-600",
    subItems: [
      { label: "Due Paid", key: "duePaid" },
      { label: "Due Pending", key: "duePending" },
    ],
  },
  {
    key: "totalCommission",
    label: "Total Commission",
    icon: FaPercentage,
    gradient: "bg-gradient-to-r from-indigo-600 to-violet-700",
    subItems: [
      { label: "Commission Paid", key: "commissionPaid" },
      { label: "Commission Pending", key: "commissionPending" },
    ],
  },
  {
    key: "totalGst",
    label: "Total GST",
    icon: FaRupeeSign,
    gradient: "bg-gradient-to-r from-orange-500 to-amber-600",
    subItems: [
      { label: "GST Paid", key: "gstPaid" },
      { label: "GST Pending", key: "gstPending" },
    ],
  },
];

const RideStatCard = ({
  cfg,
  stats,
  totalRidesCount,
  loading,
  onCardClick,
  selectedCardFilter,
}) => {
  const Icon = cfg.icon;
  const count = Number(stats[cfg.countKey]) || 0;
  const tripAmount = Number(stats[cfg.fareKey]) || 0;
  const avgFare = calcAverageFare(count, tripAmount);
  const showShare = cfg.key !== "totalRides";
  const canClick = typeof onCardClick === "function";
  const isActive =
    selectedCardFilter === cfg.key ||
    (cfg.key === "totalRides" && !selectedCardFilter);
  const classes = `${cardBaseClass} ${cfg.gradient} ${
    canClick
      ? "cursor-pointer hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-white/70"
      : ""
  } ${isActive ? "ring-4 ring-offset-2 ring-offset-white ring-slate-800 scale-[1.02]" : ""}`;

  const handleClick = () => {
    if (!canClick) return;
    onCardClick(cfg.key);
  };

  const content = (
    <>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-wider opacity-90 truncate pr-2">
          {cfg.label}
          {cfg.last24Hours ? (
            <span className="ml-1 normal-case font-semibold opacity-80">
              (24h)
            </span>
          ) : null}
        </span>
        <Icon className="text-base opacity-80 flex-shrink-0" />
      </div>
      <div className="text-lg md:text-xl font-bold leading-tight">
        {loading ? (
          <span className="inline-block h-6 w-20 rounded bg-white/30 animate-pulse" />
        ) : (
          <>
            {count.toLocaleString("en-IN")}
            {showShare && (
              <span className="text-sm font-semibold opacity-90 ml-1">
                ({formatStatusSharePercent(count, totalRidesCount)}%)
              </span>
            )}
          </>
        )}
      </div>
      <div className="mt-1.5 pt-1.5 border-t border-white/30">
        <div className="text-xs font-semibold opacity-95">
          {loading ? (
            <span className="inline-block h-4 w-28 rounded bg-white/30 animate-pulse" />
          ) : (
            <>
              {formatMoney(tripAmount)}
              <span className="text-[10px] font-medium opacity-90 ml-1">
                ({formatMoney(avgFare)} avg)
              </span>
            </>
          )}
        </div>
      </div>
    </>
  );

  if (!canClick) {
    return <div className={classes}>{content}</div>;
  }

  return (
    <button
      type="button"
      className={`${classes} text-left w-full`}
      title={cfg.label}
      onClick={handleClick}
      aria-label={`Filter rides by ${cfg.label}`}
      aria-pressed={isActive}
    >
      {content}
    </button>
  );
};

const FinancialSummaryCard = ({ cfg, stats, loading, nested = false }) => {
  const Icon = cfg.icon;
  const subItems = cfg.subItems || [];
  const subTotal = subItems.reduce(
    (sum, item) => sum + (Number(stats[item.key]) || 0),
    0
  );
  const headerTotal =
    subItems.length === 1
      ? Number(stats[subItems[0].key]) || 0
      : subTotal;

  const shellClass = nested
    ? "rounded-md bg-white/12 border border-white/25 p-2.5 text-left w-full"
    : `${cardBaseClass} ${cfg.gradient} text-left w-full`;

  return (
    <div className={shellClass}>
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-baseline gap-1.5 min-w-0">
          <span className="text-[10px] uppercase tracking-wider text-white/95 truncate font-semibold">
            {cfg.label}
          </span>
          <span className="text-xs font-semibold text-white whitespace-nowrap">
            {loading ? (
              <span className="inline-block h-3 w-12 rounded bg-white/30 animate-pulse" />
            ) : (
              <span className="text-white/90">({formatMoney(headerTotal)})</span>
            )}
          </span>
        </div>
        <span className="h-5 w-5 rounded-full bg-white/20 border border-white/30 inline-flex items-center justify-center flex-shrink-0">
          <Icon className="text-[10px] text-white" />
        </span>
      </div>
      <div
        className={`grid gap-2 ${
          subItems.length === 1 ? "grid-cols-1" : "grid-cols-2"
        }`}
      >
        {subItems.map((item) => (
          <div
            key={item.key}
            className="rounded-md bg-white/14 border border-white/20 px-2 py-1.5 text-center"
          >
            <p className="text-[10px] leading-3.5 text-white/90 truncate font-medium">
              {item.label}
            </p>
            <p className="text-sm font-bold leading-tight mt-0.5 text-white">
              {loading ? (
                <span className="inline-block h-5 w-14 rounded bg-white/30 animate-pulse" />
              ) : (
                formatMoney(stats[item.key])
              )}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

const CompletedSectionCard = ({
  stats,
  totalRidesCount,
  loading,
  onCardClick,
  selectedCardFilter,
}) => {
  const Icon = completedCard.icon;
  const count = Number(stats[completedCard.countKey]) || 0;
  const tripAmount = Number(stats[completedCard.fareKey]) || 0;
  const avgFare = calcAverageFare(count, tripAmount);
  const canClick = typeof onCardClick === "function";
  const isActive = selectedCardFilter === completedCard.key;
  const headerClasses = `flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-white/30 ${
    canClick
      ? "cursor-pointer rounded-md -mx-1 px-1 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white/70"
      : ""
  } ${isActive ? "ring-2 ring-white/80 ring-offset-2 ring-offset-green-600 rounded-md" : ""}`;

  const handleHeaderClick = () => {
    if (!canClick) return;
    onCardClick(completedCard.key);
  };

  const headerContent = (
    <>
      <div className="flex items-center gap-2 min-w-0">
        <span className="h-8 w-8 rounded-full bg-white/20 border border-white/30 inline-flex items-center justify-center flex-shrink-0">
          <Icon className="text-sm text-white" />
        </span>
        <span className="text-xs uppercase tracking-wider opacity-95 font-semibold">
          {completedCard.label}
        </span>
      </div>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <div className="text-xl md:text-2xl font-bold leading-tight">
          {loading ? (
            <span className="inline-block h-7 w-24 rounded bg-white/30 animate-pulse" />
          ) : (
            <>
              {count.toLocaleString("en-IN")} rides
              <span className="text-base font-semibold opacity-90 ml-2">
                ({formatStatusSharePercent(count, totalRidesCount)}%)
              </span>
            </>
          )}
        </div>
        <div className="text-sm font-semibold opacity-95 md:border-l md:border-white/30 md:pl-4">
          {loading ? (
            <span className="inline-block h-5 w-32 rounded bg-white/30 animate-pulse" />
          ) : (
            <>
              Trip amount: {formatMoney(tripAmount)}
              <span className="text-xs font-medium opacity-90 ml-2">
                (Avg {formatMoney(avgFare)})
              </span>
            </>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div
      className={`${cardBaseClass} ${completedCard.gradient} w-full`}
    >
      {canClick ? (
        <button
          type="button"
          className={`${headerClasses} w-full text-left text-white`}
          onClick={handleHeaderClick}
          aria-label={`Filter rides by ${completedCard.label}`}
          aria-pressed={isActive}
        >
          {headerContent}
        </button>
      ) : (
        <div className={headerClasses}>{headerContent}</div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-3">
        {completedFinancialCards.map((cfg) => (
          <FinancialSummaryCard
            key={cfg.key}
            cfg={cfg}
            stats={stats}
            loading={loading}
            nested
          />
        ))}
      </div>
    </div>
  );
};

const DriverRideStatsCards = ({
  stats,
  loading = false,
  selectedCardFilter = null,
  onCardClick,
}) => {
  const breakdown = stats || emptyDriverStatusBreakdown();
  const totalRidesCount =
    Number(breakdown.totalRides) ||
    statusRow1.reduce((sum, cfg) => sum + (Number(breakdown[cfg.countKey]) || 0), 0);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
          Ride Statistics
        </h3>
        <p className="text-[11px] text-gray-500 mb-2">
          Click a card to filter the rides list below.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          {statusRow1.map((cfg) => (
            <RideStatCard
              key={cfg.key}
              cfg={cfg}
              stats={breakdown}
              totalRidesCount={totalRidesCount}
              loading={loading}
              onCardClick={onCardClick}
              selectedCardFilter={selectedCardFilter}
            />
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <RideStatCard
            cfg={startedCard}
            stats={breakdown}
            totalRidesCount={totalRidesCount}
            loading={loading}
            onCardClick={onCardClick}
            selectedCardFilter={selectedCardFilter}
          />
          {cancellationCards.map((cfg) => (
            <RideStatCard
              key={cfg.key}
              cfg={cfg}
              stats={breakdown}
              totalRidesCount={totalRidesCount}
              loading={loading}
              onCardClick={onCardClick}
              selectedCardFilter={selectedCardFilter}
            />
          ))}
        </div>
      </div>

      <div>
        <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
          Completed
        </h4>
        <CompletedSectionCard
          stats={breakdown}
          totalRidesCount={totalRidesCount}
          loading={loading}
          onCardClick={onCardClick}
          selectedCardFilter={selectedCardFilter}
        />
      </div>
    </div>
  );
};

export default DriverRideStatsCards;
